import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import PageHeader from './PageHeader';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,

  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { format } from 'date-fns';

const AnalyticsPage = () => {
  const [timeRange, setTimeRange] = useState('today');
  const [analyticsData, setAnalyticsData] = useState({
    totalSales: 0,
    totalOrders: 0,
    totalCustomers: 0,
    avgOrderValue: 0,
    topProducts: [],
    salesByDate: [],
    revenueByCategory: []
  });

  useEffect(() => {
    // Fetch analytics data based on time range
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    // Mock data - replace with actual API calls
    const mockData = {
      totalSales: 15420.50,
      totalOrders: 143,
      totalCustomers: 89,
      avgOrderValue: 107.83,
      topProducts: [
        { name: 'Product A', sold: 45, revenue: 2250 },
        { name: 'Product B', sold: 32, revenue: 1920 },
        { name: 'Product C', sold: 28, revenue: 1680 },
        { name: 'Product D', sold: 23, revenue: 1380 },
        { name: 'Product E', sold: 15, revenue: 900 }
      ],
      salesByDate: [
        { date: '2025-01-01', sales: 1200, orders: 12 },
        { date: '2025-01-02', sales: 1800, orders: 18 },
        { date: '2025-01-03', sales: 2100, orders: 21 },
        { date: '2025-01-04', sales: 1650, orders: 16 },
        { date: '2025-01-05', sales: 2300, orders: 23 }
      ],
      revenueByCategory: [
        { category: 'Electronics', revenue: 5200, percentage: 33.7 },
        { category: 'Clothing', revenue: 4100, percentage: 26.6 },
        { category: 'Food & Beverages', revenue: 3800, percentage: 24.6 },
        { category: 'Books', revenue: 2320, percentage: 15.1 }
      ]
    };
    setAnalyticsData(mockData);
  };

  const StatCard = ({ title, value, subtitle, color = 'primary' }) => (
    <Card elevation={3} sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" color={`${color}.main`} gutterBottom>
          {title}
        </Typography>
        <Typography variant="h3" fontWeight="bold" color="text.primary">
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <PageHeader title="Analytics" subtitle="Data insights and performance metrics" />
      
      <Box sx={{ p: 3 }}>
        {/* Time Range Selector */}
        <Box sx={{ mb: 3 }}>
          <FormControl variant="outlined" size="small">
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              label="Time Range"
            >
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">This Week</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="year">This Year</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Key Metrics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Sales"
              value={`$${analyticsData.totalSales.toLocaleString()}`}
              subtitle="Revenue generated"
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Orders"
              value={analyticsData.totalOrders}
              subtitle="Orders processed"
              color="secondary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Customers"
              value={analyticsData.totalCustomers}
              subtitle="Unique customers"
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Avg Order Value"
              value={`$${analyticsData.avgOrderValue.toFixed(2)}`}
              subtitle="Per order"
              color="success"
            />
          </Grid>
        </Grid>

        {/* Data Tables */}
        <Grid container spacing={3}>
          {/* Top Products */}
          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top Products
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Sold</TableCell>
                        <TableCell align="right">Revenue</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analyticsData.topProducts.map((product, index) => (
                        <TableRow key={index}>
                          <TableCell>{product.name}</TableCell>
                          <TableCell align="right">{product.sold}</TableCell>
                          <TableCell align="right">${product.revenue}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Sales by Date */}
          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sales by Date
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Sales</TableCell>
                        <TableCell align="right">Orders</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analyticsData.salesByDate.map((day, index) => (
                        <TableRow key={index}>
                          <TableCell>{format(new Date(day.date), 'MMM dd')}</TableCell>
                          <TableCell align="right">${day.sales}</TableCell>
                          <TableCell align="right">{day.orders}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Revenue by Category */}
          <Grid item xs={12}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Revenue by Category
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Revenue</TableCell>
                        <TableCell align="right">Percentage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analyticsData.revenueByCategory.map((category, index) => (
                        <TableRow key={index}>
                          <TableCell>{category.category}</TableCell>
                          <TableCell align="right">${category.revenue}</TableCell>
                          <TableCell align="right">{category.percentage}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
};

export default AnalyticsPage;
